import { PortalIdentity } from './shell/PortalIdentity';
import { Button } from './ui/Button';

export interface SessionExpiredViewProps {
  reason: 'timeout' | 'revoked';
  onReauthenticate: () => void;
}

/**
 * Accessible session-expired prompt (AC3; PRO-0020). Shown when the 8h age cap
 * trips or a re-validation returns 401. Reskinned (PRO-0260 token-first) to mirror
 * the unauthenticated `LandingView` card so re-authentication feels like a
 * continuation of the sign-in experience, not a bare error page. Owns its own
 * single `<main>` landmark (like `LandingView`) so the AccessGate frame is not
 * needed — keeps exactly one `main` on the branch (NF-10). No technical detail
 * (SEC-8); single primary "Sign in again" action calling `loginRedirect`.
 */
export function SessionExpiredView({ reason, onReauthenticate }: SessionExpiredViewProps) {
  const body =
    reason === 'timeout'
      ? 'Your sign-in session has reached the 8-hour security limit. Sign in again to continue.'
      : 'Your access has changed and your current session is no longer valid. Sign in again to continue.';

  return (
    <main className="mx-auto flex min-h-screen max-w-narrow flex-col justify-center px-4 py-16">
      <div className="rounded-lg border border-border bg-surface p-8 shadow-md">
        <PortalIdentity className="mb-6" />
        <section aria-labelledby="session-expired-heading" role="status">
          <h1
            id="session-expired-heading"
            className="m-0 mb-2 text-xl font-bold tracking-tight text-text-primary"
          >
            Session expired
          </h1>
          <p className="mb-6 text-base text-text-secondary">{body}</p>
          <Button onClick={onReauthenticate}>Sign in again</Button>
        </section>
      </div>
    </main>
  );
}
